// ✅ Usage tracking and minute limit enforcement for web platform
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { getCurrentBillingCycle } from "./billingCycleUtils";
import { getMonthlyMinutes } from "../config/pricing";

// Plan minute limits - now using centralized pricing config
export const PLAN_LIMITS = {
  'free': 300,              // 300 minutes every month (first month and recurring)
  'Free': 300,              // Same as 'free' for case variations
  'starter_monthly': 600,   // 600 minutes for starter ($14.99/month)
  'pro_monthly': 2500,      // 2500 minutes for pro ($49.99/month)
  'unlimited_monthly': 999999    // Effectively unlimited for unlimited plan ($99.99/month)
};

// Get the appropriate free plan limit. Free plan is now 300 minutes every month —
// first month and every following month — per Camyvera pricing as of 2026-04-28.
export const getFreePlanLimit = (userData) => {
  if (!userData) return 300;
  return userData.monthlyFreeMinutes || userData.trialMinutesLimit || 300;
};

// Get current billing cycle key for usage tracking (billing cycle-aware)
export const getCurrentMonth = (userData = null) => {
  // Use billing cycle if user has paid subscription
  if (userData && userData.subscriptionStartDate && userData.planId !== 'free') {
    const billingCycle = getCurrentBillingCycle(userData);
    if (billingCycle) return billingCycle;
  }
  // Fallback to calendar month for free users or if billing cycle can't be determined
  return new Date().toISOString().slice(0, 7); // Returns "2025-11" format
};

// Track call usage after a call ends (delegates to server API for cross-platform consistency)
export const trackCallUsage = async (userId, callDurationMinutes, callData = {}) => {
  try {
    if (!userId || !callDurationMinutes) {
      return false;
    }

    const payload = {
      userId,
      durationMinutes: callDurationMinutes,
      durationSeconds: callData.durationSeconds || (callDurationMinutes * 60),
      expertId: callData.expertId || null,
      amount: callData.amount ?? callData.cost ?? 0,
      callId: callData.callId || callData.callLogId || null,
      callLogId: callData.callLogId || null,
      platform: callData.platform || 'web',
      source: callData.source || 'frontend-track'
    };

    const response = await fetch('/api/usage/record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok || !result?.success) {
      console.error('❌ Failed to track usage via API:', result?.error || response.statusText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('❌ Error tracking call usage:', error);
    return false;
  }
};

// Check if user can make a call based on minute limits
// ✅ Seeker monthly-subscription minute limits are DISABLED — seekers get
// unlimited minutes. Per-minute billing (balance) is unchanged. Flip this to
// true to restore the monthly plan limits.
const SEEKER_MINUTE_LIMITS_ENABLED = false;

export const checkMinuteLimit = async (userId, userData = null) => {
  if (!SEEKER_MINUTE_LIMITS_ENABLED) {
    return {
      canCall: true,
      planId: userData?.planId || 'free',
      used: 0,
      limit: 'unlimited',
      remaining: 'unlimited',
      isUnlimited: true,
      percentUsed: 0,
    };
  }
  try {
    // Get user data if not provided
    if (!userData) {
      const userDoc = await getDoc(doc(db, "users", userId));
      userData = userDoc.exists() ? userDoc.data() : null;
    }
    
    if (!userData) {
      return {
        canCall: false,
        error: 'User data not found',
        used: 0,
        limit: 0,
        remaining: 0
      };
    }
    
    const planId = userData.planId || 'free';
    // For free plan, use dynamic limit based on signup date
    const planLimit = planId === 'free' ? getFreePlanLimit(userData) : (PLAN_LIMITS[planId] || 0);
    const currentMonth = getCurrentMonth(userData);
    const usedMinutes = userData.monthlyUsage?.[currentMonth] || 0;
    
    
    // Unlimited plan
    if (planLimit === -1) {
      return {
        canCall: true,
        planId,
        used: usedMinutes,
        limit: "unlimited",
        remaining: "unlimited",
        isUnlimited: true
      };
    }
    
    // Check if user has exceeded limit
    const remaining = Math.max(0, planLimit - usedMinutes);
    const canCall = usedMinutes < planLimit;
    
    return {
      canCall,
      planId,
      used: usedMinutes,
      limit: planLimit,
      remaining,
      isUnlimited: false,
      percentUsed: Math.round((usedMinutes / planLimit) * 100)
    };
    
  } catch (error) {
    console.error('❌ Error checking minute limits:', error);
    return {
      canCall: false,
      error: error.message,
      used: 0,
      limit: 0,
      remaining: 0
    };
  }
};

// Get usage statistics for display
export const getUsageStats = async (userId, userData = null) => {
  try {
    if (!userData) {
      const userDoc = await getDoc(doc(db, "users", userId));
      userData = userDoc.exists() ? userDoc.data() : null;
    }
    
    if (!userData) return null;

    const currentMonth = getCurrentMonth(userData);
    const planId = userData.planId || 'free';
    // For free plan, use dynamic limit based on signup date
    const planLimit = planId === 'free' ? getFreePlanLimit(userData) : (PLAN_LIMITS[planId] || 0);
    const usedMinutes = userData.monthlyUsage?.[currentMonth] || 0;
    const totalMinutes = userData.totalCallMinutes || 0;
    
    // Get previous month usage for comparison
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const lastMonthKey = lastMonth.toISOString().slice(0, 7);
    const lastMonthUsage = userData.monthlyUsage?.[lastMonthKey] || 0;
    
    const stats = {
      currentMonth: {
        used: usedMinutes,
        limit: planLimit === -1 ? "unlimited" : planLimit,
        remaining: planLimit === -1 ? "unlimited" : Math.max(0, planLimit - usedMinutes),
        percentUsed: planLimit === -1 ? 0 : Math.round((usedMinutes / planLimit) * 100)
      },
      lastMonth: {
        used: lastMonthUsage
      },
      allTime: {
        totalMinutes: totalMinutes
      },
      planInfo: {
        planId,
        planName: getPlanDisplayName(planId),
        isUnlimited: planLimit === -1
      }
    };
    
    return stats;
    
  } catch (error) {
    console.error('❌ Error getting usage stats:', error);
    return null;
  }
};

// Group Sessions are FREE TO HOST on Camyvera — flat guest cap for everyone,
// regardless of plan. 20 guests + 1 host = 21 total participants.
export const getParticipantLimit = (_planId) => {
  return 20;
};

// Helper to get plan display name
export const getPlanDisplayName = (planId) => {
  const planNames = {
    'free': 'Free Plan',
    'starter_monthly': 'Starter Plan',
    'pro_monthly': 'Pro Plan',
    'unlimited_monthly': 'Unlimited Plan'
  };
  
  return planNames[planId] || 'Unknown Plan';
};

// Check if user should be warned about approaching limit
export const shouldWarnAboutLimit = (usageStats) => {
  if (!usageStats || usageStats.planInfo.isUnlimited) return false;
  
  const percentUsed = usageStats.currentMonth.percentUsed;
  return percentUsed >= 80; // Warn when 80% used
};

// Get usage warning message
export const getUsageWarning = (usageStats) => {
  if (!shouldWarnAboutLimit(usageStats)) return null;
  
  const remaining = usageStats.currentMonth.remaining;
  const percentUsed = usageStats.currentMonth.percentUsed;
  
  if (remaining === 0) {
    return {
      type: 'error',
      title: 'Monthly Limit Reached',
      message: 'You have used all your monthly minutes. Upgrade your plan to continue making calls.'
    };
  } else if (percentUsed >= 90) {
    return {
      type: 'warning',
      title: 'Almost Out of Minutes',
      message: `You have ${remaining} minutes remaining this month. Consider upgrading to avoid interruptions.`
    };
  } else {
    return {
      type: 'info',
      title: 'Approaching Limit',
      message: `You have used ${percentUsed}% of your monthly minutes. ${remaining} minutes remaining.`
    };
  }
};

// Reset usage for new billing cycle (called by webhook or cron job)
export const resetMonthlyUsage = async (userId, userData = null) => {
  try {
    if (!userData) {
      const userDoc = await getDoc(doc(db, "users", userId));
      userData = userDoc.exists() ? userDoc.data() : null;
    }

    const currentMonth = getCurrentMonth(userData);

    await updateDoc(doc(db, "users", userId), {
      [`monthlyUsage.${currentMonth}`]: 0
    });

    return true;
  } catch (error) {
    console.error('❌ Error resetting monthly usage:', error);
    return false;
  }
};

// Get user's call history from new collection
export const getUserCallHistory = async (userId, monthFilter = null, limit = 50) => {
  try {
    const { query, where, orderBy, limitToLast, getDocs } = await import("firebase/firestore");
    
    let callHistoryQuery = query(
      collection(db, "userCallHistory"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    // Filter by month if specified
    if (monthFilter) {
      callHistoryQuery = query(
        collection(db, "userCallHistory"),
        where("userId", "==", userId),
        where("month", "==", monthFilter),
        orderBy("createdAt", "desc")
      );
    }
    
    // Apply limit
    if (limit > 0) {
      callHistoryQuery = query(callHistoryQuery, limitToLast(limit));
    }
    
    const snapshot = await getDocs(callHistoryQuery);
    const callHistory = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firebase timestamps to dates
      date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date,
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
    }));
    
    return callHistory;
  } catch (error) {
    console.error('❌ Error getting call history:', error);
    return [];
  }
};

// Reset call history for a specific month (admin function)
export const resetMonthlyCallHistory = async (userId, monthToReset = null) => {
  try {
    const { query, where, getDocs, writeBatch } = await import("firebase/firestore");
    
    const targetMonth = monthToReset || getCurrentMonth();
    
    // Query all call history records for the specified month
    const historyQuery = query(
      collection(db, "userCallHistory"),
      where("userId", "==", userId),
      where("month", "==", targetMonth)
    );
    
    const snapshot = await getDocs(historyQuery);
    
    if (snapshot.empty) {
      return true;
    }
    
    // Batch delete all records for the month
    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnapshot) => {
      batch.delete(docSnapshot.ref);
    });
    
    await batch.commit();
    
    return true;
  } catch (error) {
    console.error('❌ Error resetting monthly call history:', error);
    return false;
  }
};

// ✅ Reset usage when user upgrades plan
export const resetUsageOnPlanUpgrade = async (userId, newPlanId, subscriptionStartDate = null) => {
  try {
    if (!userId || !newPlanId) {
      return false;
    }

    // Verify user document exists first
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      console.error('❌ User document not found for usage reset:', userId.slice(-6));
      return false;
    }

    const userData = userDoc.data();

    // If subscriptionStartDate provided, update it
    if (subscriptionStartDate) {
      await updateDoc(userDocRef, {
        subscriptionStartDate: subscriptionStartDate,
        planId: newPlanId
      });
      // Reload userData with new subscription date
      const updatedDoc = await getDoc(userDocRef);
      Object.assign(userData, updatedDoc.data());
    }

    const currentMonth = getCurrentMonth(userData);

    // Reset current billing cycle usage to 0 when upgrading plan
    const resetData = {
      [`monthlyUsage.${currentMonth}`]: 0,
      planUpgradeDate: serverTimestamp(),
      [`planHistory.${currentMonth}`]: {
        planId: newPlanId,
        upgradeDate: serverTimestamp(),
        usageResetTo: 0,
        resetReason: 'plan_upgrade'
      }
    };

    await updateDoc(userDocRef, resetData);

    return true;
  } catch (error) {
    console.error('❌ Error resetting usage on plan upgrade:', error);
    return false;
  }
};
